// État d'extraction d'items dérivé côté client (Partie B3/B4 — cohérence de
// statut + UI « extraction limpide »).
//
// Le statut `captures.status` ne dit PAS si l'extraction d'items a réussi : une
// capture peut être `ready` (transcript écrit) mais sans aucun item parce que la
// chaîne async d'extraction a été coupée en silence (bug 2026-05-27), OU parce
// qu'un batch async est encore en cours, OU parce que l'extraction a légitimement
// trouvé 0 besoin. On dérive donc un état d'extraction EXPLICITE à partir de
// plusieurs signaux : statut capture, présence d'un transcript, nombre d'items,
// existence d'un batch actif, succès/échec d'une ai_run ingest et son compteur
// de tentatives.

import type { CaptureStatus } from "./schema";

export type ExtractionState =
  | "none" // pas de transcript → rien à extraire (encore)
  | "pending" // pipeline amont en cours (recording/uploading/transcribing) → patienter
  | "queued" // analyse en cours côté serveur : batch actif OU reprise auto imminente
  | "stalled" // transcript prêt mais 0 item et pas d'échec tracé → calé en silence
  | "failed" // une ai_run ingest a échoué et les tentatives sont épuisées
  | "empty" // extraction RÉUSSIE mais 0 item : aucun besoin détecté (légitime)
  | "extracted"; // au moins un item présent → sain

// Plafond de tentatives d'ingest avant de considérer l'extraction comme
// définitivement échouée. Tant qu'on est en-dessous, le cron de réconciliation
// (reconcile-extraction, */5) va réessayer → on affiche « en file », pas « échec ».
// Doit rester aligné avec `maxAttempts` du cron (reconcile-extraction.ts) et
// `MAX_TRANSCRIBE_ATTEMPTS` côté edge.
export const MAX_INGEST_ATTEMPTS = 4;

// Fenêtre de grâce avant de conclure qu'une extraction a « calé ». Dans le
// chemin Replicate+batch, `status=ready` est écrit quelques secondes AVANT que
// la ligne llm_batches existe ; sans grâce, le client classait à tort cette
// fenêtre en `stalled` (« à reprendre ») — bug 2D. Aligné en esprit avec le
// délai du cron reconcile-extraction (10 min côté serveur, plus permissif).
export const EXTRACTION_GRACE_S = 180;

// Plafond de l'attente « En file » quand aucun item n'est encore arrivé
// (SIG-261002-082836). La file la plus lente est celle du lot économique :
// OpenAI garantit une réponse sous 24 h, après quoi le lot expire et le cron le
// referme. Passé 24 h + 2 h de marge SANS AUCUN SIGNE DE VIE de la file (cf.
// `secondsSinceQueueActivity`), plus rien n'arrivera tout seul — un batch resté
// « actif » en base, ou un échec que le cron ne reprend plus — et continuer
// d'afficher « En file » serait mentir : la capture passe « À reprendre », avec
// le bouton de relance.
export const QUEUE_STALL_S = 26 * 3600;

export interface ExtractionStatusInput {
  captureStatus: CaptureStatus;
  /** transcript_raw non vide. */
  hasTranscript: boolean;
  /** Nombre d'items NON soft-deleted. */
  itemCount: number;
  /** Une ai_run d'extraction (kind=transcribe, prompt_version=ingest) est `failed`. */
  ingestRunFailed: boolean;
  /** Un batch `llm_batches` submitted/in_progress existe pour cette capture. */
  hasActiveBatch: boolean;
  /** Une ai_run d'extraction (kind=transcribe, prompt_version=ingest) a réussi. */
  ingestSucceeded: boolean;
  /** Max des `attempts` des ai_runs ingest (garde anti-boucle / file vs échec). */
  ingestAttempts: number;
  /** Secondes écoulées depuis que la capture est passée `ready` (transcript écrit).
   *  Absent → pas de grâce (comportement historique). */
  secondsSinceReady?: number;
  /** Secondes écoulées depuis le DERNIER signe de vie de la file d'extraction
   *  (cf. `secondsSinceLatest`) : le passage `ready`, la soumission du lot
   *  économique, le départ du dernier run d'extraction — le plus récent des
   *  trois. Ancrer sur le seul passage `ready` ferait conclure à tort sur une
   *  capture ancienne que le cron vient de renvoyer dans le lot (il ne touche
   *  pas `captures.updated_at`). Absent → pas de plafond (historique). */
  secondsSinceQueueActivity?: number;
  /** Les items de cette capture ont-ils été chargés ? Sur la liste d'accueil, les
   *  captures arrivent AVANT les items : tant que false, `itemCount=0` est
   *  transitoire et on ne conclut PAS stalled/empty/failed (évite un flash d'état
   *  « à reprendre » au chargement). Absent/true → comportement historique. */
  itemsLoaded?: boolean;
}

// Secondes écoulées depuis le passage `ready` (= `updatedAt`, écrit juste après
// la transcription), pour alimenter la fenêtre de grâce de `deriveExtractionStatus`.
// Ne renvoie un nombre que si la capture est `ready` ET a un `updatedAt` ; sinon
// `undefined` (= pas de grâce, comportement historique sur les états non-ready).
// Source de vérité unique partagée par CaptureScreen et CapturesList.
export function secondsSinceReady(
  status: CaptureStatus,
  updatedAt: string | undefined,
  now: number = Date.now(),
): number | undefined {
  if (status !== "ready" || !updatedAt) return undefined;
  return Math.max(0, (now - new Date(updatedAt).getTime()) / 1000);
}

// Secondes écoulées depuis la PLUS RÉCENTE des dates fournies (ISO), sans
// jamais être négatif (horloge du poste en retard sur le serveur). Les dates
// absentes ou illisibles sont ignorées ; aucune date exploitable → `undefined`.
export function secondsSinceLatest(
  dates: readonly (string | null | undefined)[],
  now: number = Date.now(),
): number | undefined {
  let latest = Number.NEGATIVE_INFINITY;
  for (const d of dates) {
    if (!d) continue;
    const ms = new Date(d).getTime();
    if (!Number.isNaN(ms) && ms > latest) latest = ms;
  }
  if (latest === Number.NEGATIVE_INFINITY) return undefined;
  return Math.max(0, (now - latest) / 1000);
}

// Statuts capture qui signifient « le pipeline amont travaille encore » : tant
// qu'on y est, l'absence d'items est normale (pas un problème à signaler).
const UPSTREAM_IN_PROGRESS: ReadonlySet<CaptureStatus> = new Set([
  "recording",
  "uploading",
  "transcribing",
]);

// Attente « En file », bornée : au-delà du plafond, aucune file ne répondra
// plus, et « En file » deviendrait une promesse sans fin (SIG-261002-082836).
// Âge inconnu → pas de borne (comportement historique).
function queueOrStalled(secondsSinceQueueActivity: number | undefined): "queued" | "stalled" {
  return secondsSinceQueueActivity !== undefined && secondsSinceQueueActivity >= QUEUE_STALL_S
    ? "stalled"
    : "queued";
}

// Dérive l'état d'extraction. Ordre des règles = priorité (du plus structurel au
// plus diagnostique). Voir le tableau d'états dans `ExtractionState`.
export function deriveExtractionStatus(input: ExtractionStatusInput): ExtractionState {
  // Pas de transcript → soit le pipeline amont tourne encore, soit l'amont a
  // échoué (status=error sans transcript). Dans les deux cas l'extraction n'a
  // rien à se reprocher : "none" (pas de bouton « réessayer l'extraction »).
  if (!input.hasTranscript) return "none";

  // Amont encore en cours (recording/uploading/transcribing) → patienter.
  if (UPSTREAM_IN_PROGRESS.has(input.captureStatus)) return "pending";

  // Des items → extraction réussie (état terminal sain). Prime sur tout signal
  // résiduel : si des items existent, l'utilisateur a de quoi travailler.
  if (input.itemCount > 0) return "extracted";

  // Items pas encore chargés (liste : captures avant items) → `itemCount=0` est
  // transitoire ; on ne conclut rien (sinon flash « à reprendre » au chargement).
  // On suppose sain jusqu'à ce que les items soient connus.
  if (input.itemsLoaded === false) return "extracted";

  // À partir d'ici : transcript présent, 0 item.

  // Un batch async est en cours → analyse côté serveur, les items arriveront.
  // Échec tracé MAIS tentatives non épuisées → le cron de réconciliation va
  // réessayer sous peu : on n'affiche PAS « échec » (transitoire, auto-réparé).
  if (
    input.hasActiveBatch ||
    (input.ingestRunFailed && input.ingestAttempts < MAX_INGEST_ATTEMPTS)
  ) {
    return queueOrStalled(input.secondsSinceQueueActivity);
  }

  // Échec tracé et tentatives épuisées → échec réel, intervention requise.
  if (input.ingestRunFailed) return "failed";

  // Extraction réussie mais 0 item → aucun besoin détecté (légitime, pas une erreur).
  if (input.ingestSucceeded) return "empty";

  // L'amont a échoué (status=error) sans extraction réussie/tracée → échec.
  if (input.captureStatus === "error") return "failed";

  // Transcript prêt, 0 item, aucun batch, aucun échec/succès tracé.
  // Pendant la fenêtre de grâce post-`ready`, l'analyse côté serveur n'a peut-être
  // pas encore enregistré son batch (course connue) → `queued`, pas `stalled`.
  if (input.secondsSinceReady !== undefined && input.secondsSinceReady < EXTRACTION_GRACE_S) {
    return "queued";
  }
  // Hors grâce : la chaîne async a été coupée en silence (bug 2026-05-27).
  return "stalled";
}

// Faut-il proposer un bouton « Réessayer l'extraction » ? Uniquement quand
// l'extraction a calé (stalled) ou définitivement échoué (failed) — les deux
// états réparables par un re-déclenchement d'ingest-items. PAS pour queued
// (le serveur travaille / le cron réessaie), empty (succès, 0 besoin), pending
// (amont en cours) ni extracted (sain).
export function shouldOfferRetryExtraction(state: ExtractionState): boolean {
  return state === "stalled" || state === "failed";
}

export type ExtractionBadgeTone = "neutral" | "info" | "success" | "warning" | "danger";

export interface ExtractionBadge {
  label: string;
  tone: ExtractionBadgeTone;
}

// Badge d'extraction (libellé + ton) — brique INTERNE composée par
// `capture-phase.ts` (la source de vérité visible désormais unique). Texte seul,
// sans glyphe Unicode (DS §6.1, aligné sur les autres badges) ; terme unique
// « items » (jamais « besoins »). `none` et `extracted` portent un libellé vide :
// la ligne est masquée (none) ou le compteur « N items » est rendu
// dynamiquement à la place (extracted).
const EXTRACTION_BADGE: Record<ExtractionState, ExtractionBadge> = {
  none: { label: "", tone: "neutral" },
  pending: { label: "En attente", tone: "info" },
  queued: { label: "En file", tone: "info" },
  extracted: { label: "", tone: "success" },
  empty: { label: "Aucun item détecté", tone: "success" },
  failed: { label: "Échec", tone: "danger" },
  stalled: { label: "À reprendre", tone: "warning" },
};

export function extractionBadge(state: ExtractionState): ExtractionBadge {
  return EXTRACTION_BADGE[state];
}
